import { useContext } from "react";
import { observer } from "mobx-react";
import { ActionIcon, Button } from "@mantine/core";
import { BsFillPlayFill, BsPauseFill } from "react-icons/bs";
import { ActionButton } from "./ActionButton";
import { useStores } from "@/hooks/useStores";
import { AudioContext, AudioState } from "@/context/audio";
import { trackEvent } from "@/core/analytics";
import { getPodcastImage, ImageSizes } from "@/core/helpers";

const iconSizes = {
    large: 56,
    medium: 44,
    small: 36,
    mini: 28,
    tiny: 32,
} as const;

const iconMap = {
    play: <BsFillPlayFill size={18} />,
    pause: <BsPauseFill size={18} />,
};

export const PlayButton = observer(
    ({ episode, podcast, labels = null, size = "medium", asText = false }: PlayButtonProps) => {
        const { playerStore } = useStores();
        const sound = useContext<AudioState>(AudioContext);

        const play = (event?: React.MouseEvent<HTMLElement>) => {
            event?.preventDefault();
            const isSwitchingEpisode = playerStore.currentEpisode.sound !== episode.sound;

            if (isSwitchingEpisode) {
                if (playerStore.currentEpisode.sound !== "") {
                    sound.pause();
                }
                sound.setAudio({
                    id: episode.id.toString(),
                    src: episode.sound,
                    title: episode.title,
                    artist: podcast.name,
                    album: podcast.name,
                    artwork: getPodcastImage(podcast, ImageSizes.Medium),
                });
                playerStore.setCurrentEpisode(episode, podcast);
            }

            const currentProgress = isSwitchingEpisode ? 0 : Math.floor(sound.seek());
            trackEvent({
                category: "Player",
                action: "Play",
                label: `${podcast.slug}-${episode.slug}`,
                value: currentProgress,
            });
            playerStore.play(currentProgress);
            sound.play();
        };

        const pause = (event?: React.MouseEvent<HTMLElement>) => {
            event?.preventDefault();

            const currentProgress = parseInt(sound.seek().toString());
            trackEvent({
                category: "Player",
                action: "Pause",
                label: `${podcast.slug}-${episode.slug}`,
                value: currentProgress,
            });
            playerStore.pause(currentProgress);
            sound.pause();
        };

        const isPlaying = playerStore.currentEpisode.title === episode.title && sound.isPlaying;

        if (asText) {
            return isPlaying ? (
                <Button color="dark" leftSection={iconMap.pause} onClick={pause} size="compact-xs" variant="default">
                    Pausa
                </Button>
            ) : (
                <Button color="teal" leftSection={iconMap.play} onClick={play} size="compact-xs" variant="default">
                    Spela
                </Button>
            );
        }

        if (labels) {
            return isPlaying ? (
                <ActionButton icon={<BsPauseFill fontSize={"inherit"} />} onClick={pause} label={labels[1]} />
            ) : (
                <ActionButton icon={<BsFillPlayFill fontSize={"inherit"} />} onClick={play} label={labels[0]} />
            );
        }

        return isPlaying ? (
            <ActionIcon
                aria-label="Pausa avsnitt"
                color="pink"
                onClick={pause}
                radius="sm"
                size={iconSizes[size]}
                variant="filled">
                <BsPauseFill size={18} />
            </ActionIcon>
        ) : (
            <ActionIcon
                aria-label="Spela avsnitt"
                color="teal"
                onClick={play}
                radius="sm"
                size={iconSizes[size]}
                variant="light">
                <BsFillPlayFill size={18} />
            </ActionIcon>
        );
    }
);
